import { CircularProgress, CssBaseline, ThemeProvider } from '@mui/material'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { Toaster } from 'sonner'
import { Route, Routes } from 'react-router'
import { theme } from '~/ui/theme'
import { AdminPageLayout } from '~/ui/AdminPageLayout'
import { routes } from '~/features/routes'
import { Reports } from '~/features/sales/reports'
import { CustomerList } from '~/features/customers/CustomerList'
import { CustomerRequestList } from '~/features/customers/CustomerRequestList'
import { VisitorList } from '~/features/visitors/VisitorList'
import { VisitorFormLoader } from '~/features/visitors/VisitorFormLoader'
import { AuthProvider } from '~/providers/AuthProvider'
import { lazy, Suspense } from 'react'
import { Dashboard } from '~/features/dashboard'
import { InventoryList } from '~/features/inventory/InventoryList'
import { StoryousList } from '~/features/storyous/StoryousList'
import { ModelsList } from '~/features/models/models-list'
import { AgencyList } from '~/features/agencies/AgencyList'
import { AgencyCreateForm } from '~/features/agencies/AgencyCreateForm'
import { VisitorForm } from './features/visitors/VisitorForm'
import { OrdersList } from './features/orders/OrdersList'
import { RequireRole } from '~/features/auth/RequireRole'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: 'always',
    },
  },
})
const JsonDecoder = lazy(() => import('~/features/json-decoder'))

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <Toaster richColors toastOptions={{ closeButton: true }} />
        <CssBaseline />
        <AuthProvider>
          <Routes>
            <Route element={<AdminPageLayout />}>
              <Route path={routes.home.route} element={<Dashboard />} />

              <Route
                path={routes.createVisitor.route}
                element={
                  <RequireRole allow={routes.createVisitor.allow}>
                    <VisitorForm />
                  </RequireRole>
                }
              />

              <Route
                path={routes.editVisitor.route}
                element={
                  <RequireRole allow={routes.editVisitor.allow}>
                    <VisitorFormLoader />
                  </RequireRole>
                }
              />

              <Route
                path={routes.customers.menu.customersList.route}
                element={
                  <RequireRole allow={routes.customers.menu.customersList.allow}>
                    <CustomerList />
                  </RequireRole>
                }
              />
              <Route
                path={routes.customers.menu.customerRequests.route}
                element={
                  <RequireRole allow={routes.customers.menu.customerRequests.allow}>
                    <CustomerRequestList />
                  </RequireRole>
                }
              />

              <Route
                path={routes.inventory.menu.wollemInventory.route}
                element={
                  <RequireRole allow={routes.inventory.menu.wollemInventory.allow}>
                    <InventoryList />
                  </RequireRole>
                }
              />
              <Route
                path={routes.inventory.menu.storyousInventory.route}
                element={
                  <RequireRole allow={routes.inventory.menu.storyousInventory.allow}>
                    <StoryousList />
                  </RequireRole>
                }
              />

              <Route
                path={routes.models.menu.list.route}
                element={
                  <RequireRole allow={routes.models.menu.list.allow}>
                    <ModelsList />
                  </RequireRole>
                }
              />

              <Route
                path={routes.sales.menu.visitors.route}
                element={
                  <RequireRole allow={routes.sales.menu.visitors.allow}>
                    <VisitorList />
                  </RequireRole>
                }
              />
              <Route
                path={routes.sales.menu.reports.route}
                element={
                  <RequireRole allow={routes.sales.menu.reports.allow}>
                    <Reports />
                  </RequireRole>
                }
              />
              <Route
                path={routes.sales.menu.agencies.route}
                element={
                  <RequireRole allow={routes.sales.menu.agencies.allow}>
                    <AgencyList />
                  </RequireRole>
                }
              />
              <Route
                path={routes.sales.menu.orders.route}
                element={
                  <RequireRole allow={routes.sales.menu.orders.allow}>
                    <OrdersList />
                  </RequireRole>
                }
              />

              <Route
                path={routes.createAgency.route}
                element={
                  <RequireRole allow={routes.createAgency.allow}>
                    <AgencyCreateForm />
                  </RequireRole>
                }
              />
            </Route>
            <Route
              path="/json"
              element={
                <Suspense fallback={<CircularProgress />}>
                  <JsonDecoder />
                </Suspense>
              }
            />
          </Routes>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}
